import { useEffect, useState } from "react";
import { X, Gift } from "lucide-react";
import api from "@/lib/api";

const DISMISS_KEY = "sl_promo_dismissed_at";

/**
 * Admin-editable promo banner sitting at the very top of the page.
 * Enabled/disabled and content managed from /admin/site-content.
 * Dismissible per-session — automatically shows again once the admin
 * updates the message (banner content acts as a version key).
 */
export default function PromoBanner() {
  const [content, setContent] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/site-content");
        if (cancelled) return;
        if (!data?.promo_banner_enabled) return;
        const text = (data.promo_banner_text || "").trim();
        if (!text) return;
        const key = `${DISMISS_KEY}:${text}`;
        try {
          const dismissed = sessionStorage.getItem(key);
          if (dismissed) return;
        } catch (_e) { /* ignore */ }
        setContent(data);
        setVisible(true);
      } catch (_e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!visible || !content) return null;

  const dismiss = () => {
    setVisible(false);
    try {
      const key = `${DISMISS_KEY}:${(content.promo_banner_text || "").trim()}`;
      sessionStorage.setItem(key, String(Date.now()));
    } catch (_e) { /* ignore */ }
  };

  const link = (content.promo_banner_link || "").trim();
  const cta = (content.promo_banner_cta || "En savoir plus").trim();

  return (
    <div
      className="w-full text-white relative"
      style={{ background: "linear-gradient(90deg, #E89B1A, #d17e10)" }}
      data-testid="promo-banner"
    >
      <div className="max-w-6xl mx-auto flex items-center gap-2.5 pl-2.5 pr-9 py-2 sm:gap-4 sm:pl-4 sm:pr-10 sm:py-2.5">
        {/* Round gift icon badge */}
        <span
          className="flex-shrink-0 w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-white/25 backdrop-blur-sm flex items-center justify-center shadow-sm"
          aria-hidden="true"
        >
          <Gift size={14} strokeWidth={2.25} className="text-white sm:hidden" />
          <Gift size={18} strokeWidth={2.25} className="text-white hidden sm:block" />
        </span>

        {/* Text + CTA — flex-col on mobile so nothing gets truncated, inline on desktop */}
        <div
          className="flex-1 min-w-0 leading-tight"
          data-testid="promo-banner-text"
          style={{ fontSize: "12px" }}
        >
          <span className="font-semibold sm:font-medium sm:text-sm">{content.promo_banner_text}</span>
          {link && (
            <>
              {" "}
              <a
                href={link}
                target={link.startsWith("http") ? "_blank" : undefined}
                rel="noreferrer"
                className="inline-flex items-center gap-0.5 font-bold underline decoration-white/70 underline-offset-2 hover:decoration-white whitespace-nowrap sm:text-sm"
                data-testid="promo-banner-cta"
              >
                {cta} <span aria-hidden="true">→</span>
              </a>
            </>
          )}
        </div>

        <button
          type="button"
          onClick={dismiss}
          aria-label="Masquer"
          data-testid="promo-banner-dismiss"
          className="absolute right-1 sm:right-2 top-1/2 -translate-y-1/2 text-white/85 hover:text-white p-1.5 rounded-full hover:bg-white/10 transition-colors"
        >
          <X size={15} />
        </button>
      </div>
    </div>
  );
}
